import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { AreaChart, Area, BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, Cell } from 'recharts';
import { ChevronLeft, Moon, Timer, Utensils, Lightbulb, TrendingUp, Award, AlertTriangle } from 'lucide-react';

const RANGES = [{ d: 7, l: '7D' }, { d: 30, l: '30D' }, { d: 90, l: '90D' }];

export default function RecoveryInsights() {
  const nav = useNavigate();
  const [days, setDays] = useState(7);
  const [sleep, setSleep] = useState(null);
  const [fasting, setFasting] = useState(null);
  const [insight, setInsight] = useState(null);
  const [rec, setRec] = useState(null);

  useEffect(() => {
    api.get('/sleep-analytics', { params: { days } }).then(r => setSleep(r.data));
    api.get('/fasting-analytics', { params: { days } }).then(r => setFasting(r.data));
  }, [days]);
  useEffect(() => {
    api.get('/coach-insight').then(r => setInsight(r.data)).catch(() => {});
    api.get('/recovery-score').then(r => setRec(r.data)).catch(() => {});
  }, []);

  const breakdown = rec ? [
    { label: 'Sleep', val: rec.sleep, weight: rec.weights?.sleep, color: '#A78BFA' },
    { label: 'Fasting', val: rec.fasting, weight: rec.weights?.fasting, color: '#00C8FF' },
    { label: 'Meal Timing', val: rec.meal_timing, weight: rec.weights?.meal_timing, color: '#FFB800' },
    { label: 'Recovery Activities', val: rec.activities, weight: rec.weights?.activities, color: '#65A30D' },
  ] : [];

  return (
    <div className="space-y-5" data-testid="recovery-insights-page">
      <div className="flex items-center gap-3">
        <button onClick={() => nav(-1)} data-testid="back-btn" className="w-9 h-9 rounded-full bg-[var(--c-film)] flex items-center justify-center"><ChevronLeft className="w-5 h-5" /></button>
        <h1 className="font-display font-black text-2xl">Recovery Insights</h1>
      </div>

      <div className="flex gap-2">
        {RANGES.map(r => (
          <button key={r.d} onClick={() => setDays(r.d)} data-testid={`range-${r.d}`}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition ${days === r.d ? 'bg-[#00C8FF] text-[#081726]' : 'bg-[var(--c-film)] text-muted-foreground'}`}>{r.l}</button>
        ))}
      </div>

      {/* Recovery Score Breakdown */}
      {rec && (
        <div className="regen-card-elevated p-5" data-testid="insights-breakdown">
          <div className="flex items-center justify-between mb-3">
            <span className="font-display font-bold">Recovery Score = {rec.score}</span>
            <span className="text-xs text-muted-foreground">7-day avg {rec.avg_7d}</span>
          </div>
          <div className="space-y-3">
            {breakdown.map(b => (
              <div key={b.label} className="flex items-center gap-3">
                <div className="w-32 text-sm">{b.label}</div>
                <div className="flex-1 h-2 rounded-full bg-[var(--c-film)] overflow-hidden"><div className="h-full rounded-full" style={{ width: `${b.val}%`, background: b.color, boxShadow: `0 0 8px ${b.color}` }} /></div>
                <div className="font-mono text-sm w-8 text-right">{b.val}</div>
                <div className="text-[10px] text-muted-foreground w-8 text-right">{b.weight}%</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Coaching Insights */}
      {insight && (
        <div className="regen-card-elevated p-5" data-testid="coaching-insights">
          <div className="flex items-center gap-2 mb-2"><Lightbulb className="w-4 h-4 text-[#A78BFA]" /><span className="font-display font-bold text-[#A78BFA]">Coach Recommendation</span></div>
          <div className="text-sm text-foreground/90 leading-relaxed">{insight.insight}</div>
          {insight.secondary && <div className="text-sm text-muted-foreground leading-relaxed mt-2 pt-2 border-t border-[var(--c-hair)]">{insight.secondary}</div>}
        </div>
      )}

      {/* Sleep Trends */}
      <section className="regen-card-elevated p-5" data-testid="sleep-trends">
        <div className="flex items-center gap-2 mb-3"><Moon className="w-4 h-4 text-[#A78BFA]" /><span className="font-display font-bold">Sleep Trends</span></div>
        {sleep?.count ? (
          <>
            <div className="h-36">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={sleep.series} margin={{ top: 5, right: 5, left: -22, bottom: 0 }}>
                  <defs><linearGradient id="sleepG" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#A78BFA" stopOpacity={0.4} /><stop offset="100%" stopColor="#A78BFA" stopOpacity={0} /></linearGradient></defs>
                  <XAxis dataKey="date" tickFormatter={d => d.slice(5)} tick={{ fontSize: 9, fill: '#7E8C9E' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                  <YAxis domain={[40, 100]} tick={{ fontSize: 9, fill: '#7E8C9E' }} axisLine={false} tickLine={false} width={26} />
                  <Tooltip contentStyle={{ background: '#101E30', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, fontSize: 12 }} />
                  <Area type="monotone" dataKey="score" stroke="#A78BFA" strokeWidth={2} fill="url(#sleepG)" dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3 text-center">
              <Mini label="Avg Sleep" value={sleep.avg_duration} />
              <Mini label="Avg Quality" value={`${sleep.avg_quality}/10`} />
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <BestWorst icon={Award} color='#65A30D' title="Best Night" day={sleep.best_day} />
              <BestWorst icon={AlertTriangle} color="#FFB800" title="Worst Night" day={sleep.worst_day} />
            </div>
            <div className="mt-3 text-xs text-muted-foreground">Avg bedtime: <span className="font-mono text-foreground">{sleep.avg_bedtime}</span></div>
          </>
        ) : <Empty label="No sleep data yet" />}
      </section>

      {/* Fasting Trends */}
      <section className="regen-card-elevated p-5" data-testid="fasting-trends">
        <div className="flex items-center gap-2 mb-3"><Timer className="w-4 h-4 text-[#00C8FF]" /><span className="font-display font-bold">Fasting Trends</span></div>
        {fasting?.count ? (
          <>
            <div className="h-36">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={fasting.series} margin={{ top: 5, right: 5, left: -22, bottom: 0 }}>
                  <XAxis dataKey="date" tickFormatter={d => d.slice(5)} tick={{ fontSize: 9, fill: '#7E8C9E' }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                  <YAxis domain={[0, 20]} tick={{ fontSize: 9, fill: '#7E8C9E' }} axisLine={false} tickLine={false} width={26} />
                  <Tooltip contentStyle={{ background: '#101E30', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 10, fontSize: 12 }} formatter={(v) => [`${v}h`, 'Fasting']} />
                  <Bar dataKey="hours" radius={[4, 4, 0, 0]}>
                    {fasting.series.map((e, i) => <Cell key={i} fill={e.hours >= 15 ? '#65A30D' : e.hours >= 10 ? '#A78BFA' : '#FF5C5C'} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3 text-center">
              <Mini label="Avg Fast" value={fasting.avg_fasting} />
              <Mini label="Eating Window" value={fasting.avg_eating_window} />
            </div>
          </>
        ) : <Empty label="No fasting data yet" />}
      </section>

      {/* Meal Timing Trend */}
      <section className="regen-card-elevated p-5" data-testid="meal-timing-trend">
        <div className="flex items-center gap-2 mb-3"><Utensils className="w-4 h-4 text-[#FFB800]" /><span className="font-display font-bold">Meal Timing Trend</span></div>
        {fasting?.count ? (
          <>
            <div className="grid grid-cols-1 gap-3 text-center">
              <Mini label="Avg Last Meal" value={fasting.avg_last_meal} color="#FFB800" />
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <BestWorst icon={Award} color='#65A30D' title="Earliest" day={{ duration: fasting.meal_timing_best?.time, date: fasting.meal_timing_best?.date }} />
              <BestWorst icon={AlertTriangle} color="#FF5C5C" title="Latest" day={{ duration: fasting.meal_timing_worst?.time, date: fasting.meal_timing_worst?.date }} />
            </div>
            <div className="mt-3 flex items-start gap-2 text-xs text-muted-foreground">
              <TrendingUp className="w-4 h-4 text-[#FFB800] flex-shrink-0 mt-0.5" />
              Meal timing directly impacts sleep, recovery, insulin sensitivity, fat loss and longevity.
            </div>
          </>
        ) : <Empty label="No meal timing data yet" />}
      </section>
    </div>
  );
}

function Mini({ label, value, color }) {
  return <div><div className="label-mini">{label}</div><div className="font-mono text-lg mt-1" style={color ? { color } : {}}>{value}</div></div>;
}
function BestWorst({ icon: Icon, color, title, day }) {
  return (
    <div className="rounded-xl bg-[var(--c-film)] p-3">
      <div className="flex items-center gap-1.5"><Icon className="w-3.5 h-3.5" style={{ color }} /><span className="text-[11px] font-semibold" style={{ color }}>{title}</span></div>
      <div className="font-mono text-base mt-1">{day?.duration ?? day?.score ?? '—'}</div>
      <div className="text-[10px] text-muted-foreground">{day?.date?.slice(5) || ''}</div>
    </div>
  );
}
function Empty({ label }) { return <div className="text-center text-sm text-muted-foreground py-6">{label}</div>; }
